import { FC, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { BookingRequestDetails } from "../../redux/reducers/Properties/PropertiesSliceTypes";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import {
  CreateOrderActions,
  CreateOrderData,
  OnApproveActions,
  OnApproveData,
} from "@paypal/paypal-js";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import moment from "moment";
import { currencySymbol } from "shared/CurrencySymbol";

interface InvoiceProps {
  bookingRequestDetails: BookingRequestDetails | null;
  setShowUserInvoice: React.Dispatch<React.SetStateAction<boolean>>;
  callback?: () => void;
}

// ─── Price row ────────────────────────────────────────────────────────────────
const PriceRow = ({
  label,
  value,
  highlight = false,
  discount = false,
  tag,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  discount?: boolean;
  tag?: string;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: highlight ? "16px 20px" : "12px 20px",
      background: highlight
        ? "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)"
        : discount
        ? "rgba(34,197,94,0.06)"
        : "transparent",
      borderTop: highlight ? "1px solid rgba(255,255,255,0.08)" : "1px solid #f1f5f9",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {tag && (
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 7px",
          borderRadius: 20,
          background: "#22c55e",
          color: "white",
          letterSpacing: "0.04em",
        }}>
          {tag}
        </span>
      )}
      <span style={{
        fontSize: highlight ? 15 : 13,
        fontWeight: highlight ? 700 : 500,
        color: highlight ? "white" : discount ? "#15803d" : "#64748b",
      }}>
        {label}
      </span>
    </div>
    <span style={{
      fontSize: highlight ? 18 : 13,
      fontWeight: highlight ? 800 : 600,
      color: highlight ? "white" : discount ? "#16a34a" : "#1e293b",
      letterSpacing: highlight ? "-0.02em" : "0",
    }}>
      {discount ? `− ${value}` : value}
    </span>
  </div>
);

// ─── Info tile ────────────────────────────────────────────────────────────────
const InfoTile = ({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) => (
  <div style={{
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: "14px 16px",
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  }}>
    <div style={{
      width: 34,
      height: 34,
      borderRadius: 10,
      background: "#eff6ff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 600 }}>{value}</div>
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const Invoice: FC<InvoiceProps> = ({ bookingRequestDetails, setShowUserInvoice, callback }) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  const reserveBooking = useSelector((state: RootState) => state.payment.reserveBooking);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const totalAmount = Number(reserveBooking?.booking?.total);

  const close = useCallback(() => {
    setIsClosing(true);
    setIsOpen(false);
    setTimeout(() => setShowUserInvoice(false), 180);
  }, [setShowUserInvoice]);

  // Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [close]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const onCreateOrder = (data: CreateOrderData, actions: CreateOrderActions) => {
  return actions.order.create({
    intent: "CAPTURE",
    purchase_units: [{
  reference_id: bookingRequestDetails?.number,        // shows as Item ID
  custom_id: bookingRequestDetails?.number,           // stored in PayPal records
  amount: { value: totalAmount?.toString(), currency_code: "USD" },
  description: `${bookingRequestDetails?.property?.title} | ${bookingRequestDetails?.booked_room ?? "Room"} | ${moment.utc(bookingRequestDetails?.check_in_date).format("MMM D, YYYY")} to ${moment.utc(bookingRequestDetails?.check_out_date).format("MMM D, YYYY")}`.slice(0, 127),
  soft_descriptor: "NOMADROOF",
}],
    application_context: {
      return_url: `${window.location.origin}/success`,
      cancel_url: `${window.location.origin}/booking`,
      brand_name: "Nomadroof",
    },
  });
};

  const onApproveOrder = async (data: OnApproveData, actions: OnApproveActions, nav: NavigateFunction) => {
    if (actions.order) {
      const details = await actions.order.capture();
      if (details.status === "COMPLETED") {
        setShowUserInvoice(false);
        if (callback) callback();
        const payment_intent_id = details.purchase_units?.[0]?.payments?.captures?.[0]?.id;
        nav(`/success?payment_method=paypal&redirect_status=succeeded&payment_intent=${payment_intent_id}`);
      }
    }
  };

  const guestName =
    bookingRequestDetails?.request_by ||
    `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
    "Guest";

  const propertyImage = bookingRequestDetails?.property?.place_images?.[0];

  return createPortal(
    <>
      <style>{`
        @keyframes invoiceSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes invoiceFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          background: "rgba(2, 8, 23, 0.72)",
          backdropFilter: "blur(8px)",
          animation: "invoiceFadeIn 0.2s ease both",
          opacity: isOpen ? 1 : 0,
          transition: "opacity 0.18s ease",
          pointerEvents: isOpen ? "auto" : "none",
        }}
      >
        {/* Modal card */}
        <div
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 500,
            maxHeight: "92vh",
            overflowY: "auto",
            borderRadius: 24,
            background: "#f8fafc",
            boxShadow: "0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)",
            animation: isOpen ? "invoiceSlideUp 0.3s cubic-bezier(0.34,1.56,0.64,1) both" : "none",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 transparent",
          }}
        >
          {/* ── Close button — onMouseDown for immediate, stable response ── */}
          <button
            type="button"
            aria-label="Close"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              close();
            }}
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              zIndex: 20,
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(8px)",
              color: "white",
              transition: "background 0.15s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.28)")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.15)")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="1" y1="1" x2="13" y2="13" />
              <line x1="13" y1="1" x2="1" y2="13" />
            </svg>
          </button>

          {/* ── Header with property image ── */}
          <div style={{ position: "relative", borderRadius: "24px 24px 0 0", overflow: "hidden" }}>
            {/* Background — image or gradient */}
            {propertyImage ? (
              <>
                <img
                  src={propertyImage}
                  alt=""
                  style={{ width: "100%", height: 180, objectFit: "cover", display: "block" }}
                />
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to bottom, rgba(2,8,23,0.35) 0%, rgba(2,8,23,0.75) 100%)",
                }} />
              </>
            ) : (
              <div style={{
                height: 180,
                background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
              }} />
            )}

            {/* Decorative dots */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, opacity: 0.15, backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px", pointerEvents: "none" }} />

            {/* Header text */}
            <div style={{ position: "absolute", bottom: 20, left: 24, right: 56 }}>
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 20,
                padding: "4px 12px",
                marginBottom: 8,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.9)", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  Booking Invoice
                </span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "white", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                {bookingRequestDetails?.property?.title ?? "Your Booking"}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 3 }}>
                {[bookingRequestDetails?.property?.city, bookingRequestDetails?.property?.state].filter(Boolean).join(", ")}
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ padding: "24px 24px 28px" }}>

            {/* Guest info tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              <InfoTile
                label="Guest"
                value={guestName}
                icon={<svg width="16" height="16" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
              />
              <InfoTile
                label="Guests"
                value={`${bookingRequestDetails?.number_of_guests ?? 1} ${bookingRequestDetails?.number_of_guests === 1 ? "person" : "people"}`}
                icon={<svg width="16" height="16" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
              />
              <InfoTile
                label="Check-in"
                value={moment.utc(bookingRequestDetails?.check_in_date).format("MMM D, YYYY")}
                icon={<svg width="16" height="16" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}
              />
              <InfoTile
                label="Check-out"
                value={moment.utc(bookingRequestDetails?.check_out_date).format("MMM D, YYYY")}
                icon={<svg width="16" height="16" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}
              />
            </div>

            {/* Price breakdown */}
            <div style={{ borderRadius: 16, overflow: "hidden", border: "1px solid #e2e8f0", marginBottom: 20 }}>
              {/* Section header */}
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "13px 20px",
                  background: "white",
                  border: "none",
                  cursor: "pointer",
                  borderBottom: showDetails ? "1px solid #f1f5f9" : "none",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>
                  Price Breakdown
                </span>
                <svg
                  width="16" height="16" fill="none" stroke="#94a3b8" strokeWidth="2.5" viewBox="0 0 24 24"
                  style={{ transform: showDetails ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showDetails && (
                <div style={{ background: "white" }}>
                  <PriceRow
                    label="First month's rent"
                    value={`${currencySymbol()}${Number(bookingRequestDetails?.item_total)?.toFixed(2)}`}
                  />
                  <PriceRow
                    label="Service Fee"
                    value={`${currencySymbol()}${Number(bookingRequestDetails?.service_tax)?.toFixed(2)}`}
                  />
                  {Number(bookingRequestDetails?.extra_guest_amount) > 0 && (
                    <PriceRow
                      label="Extra guests"
                      value={`${currencySymbol()}${Number(bookingRequestDetails?.extra_guest_amount)?.toFixed(2)}`}
                    />
                  )}
                  {(bookingRequestDetails?.invoice?.expenses ?? []).map((expense: any, i: number) => (
                      <PriceRow
                        key={`exp-${i}`}
                        label={expense.category}
                        value={`${currencySymbol()}${Number(expense.amount).toFixed(2)}`}
                      />
                    ))}
                  {(bookingRequestDetails?.invoice?.discounts ?? []).map((discount: any, i: number) => (
                      <PriceRow
                        key={`dis-${i}`}
                        label={discount.description}
                        value={`${currencySymbol()}${Number(discount.amount).toFixed(2)}`}
                        discount
                        tag="DISCOUNT"
                      />
                    ))}
                  {Number(bookingRequestDetails?.tax_total) > 0 && (
                    <PriceRow
                      label="Tax"
                      value={`${currencySymbol()}${Number(bookingRequestDetails?.tax_total)?.toFixed(2)}`}
                    />
                  )}
                  <PriceRow
                    label="Total Due"
                    value={`${currencySymbol()}${Number(bookingRequestDetails?.total_amount)?.toFixed(2)}`}
                    highlight
                  />
                </div>
              )}
            </div>

            {/* Payment methods */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
                Pay with
              </div>

              {/* Credit card */}
              <button
                type="button"
                onClick={() => {
                  setShowUserInvoice(false);
                  navigate("/checkout");
                }}
                style={{
                  width: "100%",
                  padding: "15px 20px",
                  borderRadius: 14,
                  border: "none",
                  cursor: "pointer",
                  background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  boxShadow: "0 4px 20px rgba(15,23,42,0.25)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 28px rgba(15,23,42,0.35)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(15,23,42,0.25)";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="1" y="4" width="22" height="16" rx="2" />
                      <path d="M1 10h22" />
                    </svg>
                  </div>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>Credit or Debit Card</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>Visa, Mastercard, Amex</div>
                  </div>
                </div>
                <svg width="16" height="16" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                </svg>
              </button>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "14px 0" }}>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>or</span>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              </div>

              {/* PayPal */}
              {process.env.REACT_APP_PAYPAL_CLIENT_ID && (
                <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                  {!isClosing && (
                    <PayPalButtons
                      style={{ layout: "horizontal", color: "gold", shape: "rect", height: 50, label: "paypal", tagline: false }}
                      createOrder={(data, actions) => onCreateOrder(data, actions)}
                      onApprove={(data, actions) => onApproveOrder(data, actions, navigate)}
                      onCancel={() => { /* user closed PayPal popup — no action needed */ }}
                      onError={(err) => { console.error("PayPal error:", err); }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Security badge */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <svg width="13" height="13" fill="#94a3b8" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
                256-bit SSL · Payments processed securely
              </span>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default Invoice;